import React from "react";
import {
  IconButton,
  makeStyles,
  TablePagination as MaterialTablePagination,
} from "@material-ui/core";

import tableIcons from "../MaterialTableIcons";
import type { PaginatedTableQuery } from "./types";

interface TablePaginationProps {
  totalCount: number;
  query: PaginatedTableQuery;
  onQueryChange: (query: PaginatedTableQuery) => void;
  pageSizeOptions?: number[];
}

type PaginationActionsProps = {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onChangePage?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
};

const muiFontFamily = '"Roboto", "Helvetica", "Arial", "sans-serif"';

const paginationActionRangeStyle: React.CSSProperties = {
  fontFamily: muiFontFamily,
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: 1.66,
  letterSpacing: "0.03333em",
  color: "rgba(0, 0, 0, 0.54)",
};

const enabledPaginationIconColor = "rgba(0, 0, 0, 0.54)";
const disabledPaginationIconColor = "rgba(0, 0, 0, 0.26)";

const usePaginationStyles = makeStyles({
  root: {
    color: "rgba(0, 0, 0, 0.54)",
    fontFamily: muiFontFamily,
    fontSize: "0.875rem",
    overflow: "hidden",
    borderBottom: 0,
  },
  toolbar: {
    minHeight: 52,
    paddingLeft: 16,
    paddingRight: 2,
  },
  caption: {
    fontFamily: muiFontFamily,
    fontSize: "0.875rem",
    fontWeight: 400,
    lineHeight: 1.43,
    letterSpacing: "0.01071em",
    color: "rgba(0, 0, 0, 0.54)",
  },
  input: {
    fontFamily: muiFontFamily,
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
  },
  selectRoot: {
    marginLeft: 8,
    marginRight: 32,
    fontFamily: muiFontFamily,
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
  },
  select: {
    fontFamily: muiFontFamily,
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
    paddingLeft: 8,
    paddingRight: 24,
    textAlign: "right",
    textAlignLast: "right",
  },
});

const getRangeLabel = (
  page: number,
  rowsPerPage: number,
  count: number,
): string => {
  if (count === 0) return "0-0 of 0";

  const from = page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);

  return `${from}-${to} of ${count}`;
};

const scrollToTop = () => {
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    document.documentElement.scrollTo({ top: 0, behavior: "smooth" });
    document.body.scrollTo({ top: 0, behavior: "smooth" });

    Array.from(document.querySelectorAll<HTMLElement>("*"))
      .filter((element) => element.scrollTop > 0)
      .forEach((element) => {
        element.scrollTo({ top: 0, behavior: "smooth" });
      });
  }, 0);
};

const MaterialTableLikePaginationActions: React.FC<PaginationActionsProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onChangePage,
}) => {
  const lastPage = Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const handlePageChange = onPageChange ?? onChangePage;

  const FirstPageIcon: any = tableIcons.FirstPage;
  const PreviousPageIcon: any = tableIcons.PreviousPage;
  const NextPageIcon: any = tableIcons.NextPage;
  const LastPageIcon: any = tableIcons.LastPage;

  const isFirstPage = page === 0;
  const isLastPage = page >= lastPage;

  const iconColor = (disabled: boolean) =>
    disabled ? disabledPaginationIconColor : enabledPaginationIconColor;

  return (
    <div
      className="MTablePaginationInner-root"
      style={{ display: "flex", alignItems: "center", minWidth: 270 }}
    >
      <span title="First Page">
        <IconButton
          onClick={(event) => handlePageChange?.(event, 0)}
          disabled={isFirstPage}
          tabIndex={isFirstPage ? -1 : 0}
          style={{ color: iconColor(isFirstPage) }}
        >
          <FirstPageIcon />
        </IconButton>
      </span>

      <span title="Previous Page">
        <IconButton
          onClick={(event) => handlePageChange?.(event, page - 1)}
          disabled={isFirstPage}
          tabIndex={isFirstPage ? -1 : 0}
          style={{ color: iconColor(isFirstPage) }}
        >
          <PreviousPageIcon />
        </IconButton>
      </span>

      <span
        className="MuiTypography-root MuiTypography-caption"
        style={{
          ...paginationActionRangeStyle,
          flexGrow: 1,
          flexShrink: 0,
          flexBasis: "auto",
          textAlign: "center",
          alignSelf: "center",
          whiteSpace: "nowrap",
          minWidth: 90,
        }}
      >
        {getRangeLabel(page, rowsPerPage, count)}
      </span>

      <span title="Next Page">
        <IconButton
          onClick={(event) => handlePageChange?.(event, page + 1)}
          disabled={isLastPage}
          tabIndex={isLastPage ? -1 : 0}
          style={{ color: iconColor(isLastPage) }}
        >
          <NextPageIcon />
        </IconButton>
      </span>

      <span title="Last Page">
        <IconButton
          onClick={(event) => handlePageChange?.(event, lastPage)}
          disabled={isLastPage}
          tabIndex={isLastPage ? -1 : 0}
          style={{ color: iconColor(isLastPage) }}
        >
          <LastPageIcon />
        </IconButton>
      </span>
    </div>
  );
};

const TablePagination = ({
  totalCount,
  query,
  onQueryChange,
  pageSizeOptions = [10, 15, 20, 30, 100],
}: TablePaginationProps) => {
  const paginationClasses = usePaginationStyles();
  const materialPage = Math.max(query.pageNumber - 1, 0);

  return (
    <table className="w-full border-collapse bg-white">
      <tfoot>
        <tr>
          <MaterialTablePagination
            classes={{
              root: paginationClasses.root,
              toolbar: paginationClasses.toolbar,
              caption: paginationClasses.caption,
              input: paginationClasses.input,
              selectRoot: paginationClasses.selectRoot,
              select: paginationClasses.select,
            }}
            count={totalCount}
            page={materialPage}
            rowsPerPage={query.pageSize}
            rowsPerPageOptions={pageSizeOptions}
            onPageChange={(_, nextPage) => {
              onQueryChange({
                ...query,
                pageNumber: nextPage + 1,
              });
            }}
            onRowsPerPageChange={(event) => {
              const pageSize = Number(event.target.value);

              onQueryChange({
                ...query,
                pageNumber: 1,
                pageSize,
              });

              scrollToTop();
            }}
            SelectProps={{
              MenuProps: {
                disableScrollLock: true,
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
              } as any,
            }}
            ActionsComponent={MaterialTableLikePaginationActions as any}
            labelRowsPerPage=""
            labelDisplayedRows={() => ""}
          />
        </tr>
      </tfoot>
    </table>
  );
};

export default TablePagination;
